import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  House,
  ListChecks,
  PlusSquare,
  Bell,
  Users,
  SignOut,
  SunHorizon,
} from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { ROLE_LABEL } from "../lib/constants";
import { api } from "../lib/api";

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    let mounted = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/notifications", { params: { unread_only: true } });
        if (mounted) setUnread(data.length);
      } catch (_) {}
    };
    tick();
    const i = setInterval(tick, 30000);
    return () => {
      mounted = false;
      clearInterval(i);
    };
  }, [user]);

  if (!user) return null;

  const role = user.role;
  const canCreateOrder = role === "sales" || role === "super_admin";
  const showUsers = role === "super_admin";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const navItem = ({ isActive }) => `nav-item ${isActive ? "active" : ""}`;

  return (
    <div className="min-h-screen flex bg-white">
      <aside className="w-64 shrink-0 border-r border-[var(--line)] flex flex-col" data-testid="sidebar">
        <div className="px-5 py-6 border-b border-[var(--ink)]">
          <Link to="/dashboard" className="flex items-center gap-2" data-testid="brand-link">
            <SunHorizon size={28} weight="duotone" color="#2563EB" />
            <div>
              <div className="font-display font-black text-lg leading-none">SOLAR AFRICA</div>
              <div className="overline mt-1" style={{ letterSpacing: "0.3em" }}>Order Control</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 py-4">
          <NavLink to="/dashboard" className={navItem} data-testid="nav-dashboard">
            <House size={18} weight="bold" /> Dashboard
          </NavLink>
          <NavLink to="/orders" className={navItem} data-testid="nav-orders">
            <ListChecks size={18} weight="bold" /> Orders
          </NavLink>
          {canCreateOrder && (
            <NavLink to="/orders/new" className={navItem} data-testid="nav-new-order">
              <PlusSquare size={18} weight="bold" /> New Order
            </NavLink>
          )}
          <NavLink to="/notifications" className={navItem} data-testid="nav-notifications">
            <Bell size={18} weight="bold" /> Notifications
            {unread > 0 && (
              <span
                className="ml-auto px-1.5 py-0.5 text-[0.6rem] font-bold"
                style={{ background: "#2563EB", color: "#fff" }}
                data-testid="unread-count"
              >
                {unread}
              </span>
            )}
          </NavLink>
          {showUsers && (
            <NavLink to="/users" className={navItem} data-testid="nav-users">
              <Users size={18} weight="bold" /> Users
            </NavLink>
          )}
        </nav>
        <div className="border-t border-[var(--line)] p-4">
          <div className="mb-3">
            <div className="overline mb-1">Signed in as</div>
            <div className="font-display font-bold text-sm leading-tight" data-testid="current-user-name">{user.name}</div>
            <div className="text-xs text-[var(--muted)]">{user.email}</div>
            <div className="mt-2 inline-block pill" style={{ color: "#2563EB" }}>
              {ROLE_LABEL[user.role]}
            </div>
          </div>
          <button onClick={handleLogout} className="btn-ghost w-full flex items-center justify-center gap-2" data-testid="logout-btn">
            <SignOut size={16} weight="bold" /> Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 overflow-x-hidden">
        <Outlet />
      </main>
    </div>
  );
}
